import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import SyncIcon from '@mui/icons-material/Sync';

import { CDPProfile } from './types';
import {
  getSyncedResources,
  GetSyncedResourcesResponse,
  syncResources,
} from '../../services/leadManagementApi';
import { useGetCCPatientsByIdsQuery } from '../../features/lma/ccPatientApi';

import {
  PersonalInformationSection,
  TrackingParametersSection,
  CaregiverInformationSection,
  ConditionsInCDPSection,
  ProceduresInCDPSection,
  MedicationsInCDPSection,
  ImmunizationsInCDPSection,
  AllergiesInCDPSection,
  FamilyHistoryInCDPSection,
  MedicalDevicesInCDPSection,
  DeduplicationSection,
} from './full-cdp-profile';
import { getCDPProfileData } from './utils/cdpProfileNavigation';
import { LEAD_STATUS, RESOURCE_TYPES } from 'components/LeadManagement/constants';
import ConfirmationDialog from 'components/LMA/Table/ConfirmationDialog';

const StyledContainer = styled(Container)(() => ({
  padding: '0px !important',
  maxWidth: '1400px !important',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const SectionSkeleton: React.FC = React.memo(() => {
  return (
    <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
      <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 1 }}>
        {[...Array(6)].map((_, idx) => (
          <Box key={idx}>
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

SectionSkeleton.displayName = 'SectionSkeleton';

const TableSkeleton: React.FC<{ rowCount?: number }> = React.memo(({ rowCount = 3 }) => {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 1 }} />

      <Box sx={{ border: '1px solid #E9E9E9', borderRadius: '4px' }}>
        {[...Array(rowCount)].map((_, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              gap: 2,
              p: 1.5,
              borderBottom: idx < rowCount - 1 ? '1px solid #E9E9E9' : 'none',
            }}
          >
            <Skeleton variant="text" width="15%" height={20} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="30%" height={20} />
            <Skeleton variant="text" width="10%" height={20} />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

TableSkeleton.displayName = 'TableSkeleton';

const FullCDPProfile: React.FC = (props: any) => {
  const router = useRouter();
  const { id } = router.query;
  const { asPath } = router;
  const [profile, setProfile] = useState<CDPProfile | null>(
    sessionStorage.getItem('cdp-profile-data')
      ? JSON.parse(sessionStorage.getItem('cdp-profile-data') as string)
      : null
  );
  const [loadingSyncedResources, setLoadingSyncedResources] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDedup, setExpandedDedup] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [syncedResources, setSyncedResources] = useState<GetSyncedResourcesResponse | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncDialogMode, setSyncDialogMode] = useState<'confirm' | 'result'>('confirm');
  const [resultType, setResultType] = useState<'success' | 'error'>('success');
  const [syncResultMessage, setSyncResultMessage] = useState('');
  const fetchingRef = useRef(false);

  const ccPatientIds = useMemo(() => {
    return (profile?.deduplication || [])
      .map((match) => match.ccPatientReferenceId)
      .filter((id): id is string => !!id);
  }, [profile?.deduplication]);

  const shouldFetch = expandedDedup && ccPatientIds.length > 0;

  const {
    data: ccPatientsData,
    isLoading: isLoadingDedupe,
    error: dedupeError,
  } = useGetCCPatientsByIdsQuery(ccPatientIds, {
    skip: !shouldFetch,
  });

  const enrichedDeduplicationData = useMemo(() => {
    if (!ccPatientsData || !profile?.deduplication) {
      return profile?.deduplication || [];
    }

    return profile.deduplication.map((dedupeMatch) => {
      const ccData = ccPatientsData[dedupeMatch.ccPatientReferenceId];
      return {
        ...dedupeMatch,
        ...(ccData || {}),
      };
    });
  }, [profile?.deduplication, ccPatientsData]);

  useEffect(() => {
    if (!id) {
      if (
        asPath === '/ViewEnrollmentDetails/Processed' ||
        asPath === '/ViewEnrollmentDetails/Referral'
      ) {
        router.push('/');
      }
      return;
    }

    const cachedData = getCDPProfileData(id as string);

    if (cachedData) {
      const profileData = cachedData as unknown as CDPProfile;
      setError(null);
      setProfile(profileData);
      fetchSyncedResources(profileData);
    } else {
      router.push('/');
      return;
    }
  }, [id]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lma-username' || e.key === 'lma-userId') {
        const username = localStorage.getItem('lma-username');
        const userId = localStorage.getItem('lma-userId');
        if (!username && !userId) {
          router.push('/Logout');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [router]);

  const fetchSyncedResources = async (profileData: CDPProfile) => {
    if (!profileData.enrollmentId) {
      setLoadingSyncedResources(false);
      return;
    }

    if (!profileData.guestRef || !profileData.leadId) {
      setLoadingSyncedResources(false);
      return;
    }

    if (fetchingRef.current) return;
    fetchingRef.current = true;

    setLoadingSyncedResources(true);
    try {
      const syncData = await getSyncedResources({
        guestRef: profileData.guestRef,
        enrollmentId: profileData.enrollmentId,
        leadId: profileData.leadId,
        leadStatus: profileData.leadStatus || LEAD_STATUS.PROCESSED,
      });

      setSyncedResources(syncData);
    } catch (err) {
      console.error('[CDP Profile] Error fetching synced resources:', err);
      setSyncedResources({
        conditions: [],
        procedures: [],
        medications: [],
        immunizations: [],
        allergies: [],
        devices: [],
        familyMemberHistory: [],
      } as GetSyncedResourcesResponse);
    } finally {
      setLoadingSyncedResources(false);
      fetchingRef.current = false;
    }
  };

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleCheckboxChange = useCallback((itemId: string, itemType: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      const key = `${itemType}-${itemId}`;
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleSyncClick = useCallback(() => {
    if (selectedItems.size === 0 || !profile) return;
    setSyncDialogMode('confirm');
    setShowSyncDialog(true);
  }, [selectedItems, profile]);

  const handleSync = useCallback(async () => {
    if (selectedItems.size === 0 || !profile) return;

    setShowSyncDialog(false);
    setIsSyncing(true);
    try {
      const itemsByType: Record<string, string[]> = {};
      Array.from(selectedItems).forEach((key) => {
        const [type, id] = key.split('-');
        if (!itemsByType[type]) {
          itemsByType[type] = [];
        }
        itemsByType[type].push(id);
      });

      const resources: any = {
        conditions: null,
        procedures: null,
        medications: null,
        immunizations: null,
        allergies: null,
        devices: null,
        familyMemberHistory: null,
      };

      if ((itemsByType.condition || itemsByType.conditions) && syncedResources?.conditions) {
        const conditionIds = itemsByType.condition || itemsByType.conditions;
        resources.conditions = syncedResources.conditions
          .filter((item) => conditionIds.includes(String(item.conditionId)))
          .map((item) => ({
            conditionId: item.conditionId!,
            startDate: (item as any).startDate || null,
            endDate: (item as any).endDate || null,
            ageAtOnset: (item as any).ageAtOnset || null,
            comment: (item as any).comment || null,
          }));
      }

      if ((itemsByType.procedure || itemsByType.procedures) && syncedResources?.procedures) {
        const procedureIds = itemsByType.procedure || itemsByType.procedures;
        resources.procedures = syncedResources.procedures
          .filter((item) => procedureIds.includes(String(item.procedureId)))
          .map((item) => ({
            procedureId: item.procedureId!,
            comment: (item as any).comment || null,
            cptCode: (item as any).cptCode || null,
            procedureBy: (item as any).procedureBy || null,
            date: (item as any).date || null,
          }));
      }

      if ((itemsByType.medication || itemsByType.medications) && syncedResources?.medications) {
        const medicationIds = itemsByType.medication || itemsByType.medications;
        resources.medications = syncedResources.medications
          .filter((item) => medicationIds.includes(String(item.id)))
          .map((item) => ({
            id: item.id!,
            dosage: (item as any).dosage || null,
            startDate: (item as any).startDate || null,
            endDate: (item as any).endDate || null,
            comment: (item as any).comment || null,
            routeId: (item as any).routeId || null,
          }));
      }

      if (
        (itemsByType.immunization || itemsByType.immunizations) &&
        syncedResources?.immunizations
      ) {
        const immunizationIds = itemsByType.immunization || itemsByType.immunizations;
        resources.immunizations = syncedResources.immunizations
          .filter((item) => immunizationIds.includes(String(item.immunizationId)))
          .map((item) => ({
            immunizationId: item.immunizationId!,
            comment: (item as any).comment || null,
            location: (item as any).location || null,
            date: (item as any).date || null,
          }));
      }

      if ((itemsByType.allergy || itemsByType.allergies) && syncedResources?.allergies) {
        const allergyIds = itemsByType.allergy || itemsByType.allergies;
        resources.allergies = syncedResources.allergies
          .filter((item) => allergyIds.includes(String(item.allergyId)))
          .map((item) => ({
            allergyId: item.allergyId!,
            reaction: (item as any).reaction || null,
            comment: (item as any).comment || null,
            startDate: (item as any).startDate || null,
            endDate: (item as any).endDate || null,
          }));
      }

      if ((itemsByType.device || itemsByType.devices) && syncedResources?.devices) {
        const deviceIds = itemsByType.device || itemsByType.devices;
        resources.devices = syncedResources.devices
          .filter((item) => deviceIds.includes(String(item.deviceId)))
          .map((item) => ({
            deviceId: item.deviceId!,
            comment: (item as any).comment || null,
          }));
      }

      const totalAvailableResources =
        (syncedResources?.conditions?.filter((item) => !item.synced).length || 0) +
        (syncedResources?.procedures?.filter((item) => !item.synced).length || 0) +
        (syncedResources?.medications?.filter((item) => !item.synced).length || 0) +
        (syncedResources?.immunizations?.filter((item) => !item.synced).length || 0) +
        (syncedResources?.allergies?.filter((item) => !item.synced).length || 0) +
        (syncedResources?.devices?.filter((item) => !item.synced).length || 0);

      const allResourcesSelected =
        selectedItems.size >= totalAvailableResources && totalAvailableResources > 0;

      const payload = {
        ccId: profile.patientCCId || '',
        leadId: profile.leadId || 0,
        userId: localStorage.getItem('lma-userId') || '1',
        guestRef: profile.guestRef || '',
        enrollmentId: profile.enrollmentId || '',
        allResourcesSelected,
        resources,
      };

      const response = await syncResources(payload);

      const resourcesConfirmation = response.resources || {};

      const failedResources: string[] = [];
      const successResources: string[] = [];
      const errorMessages: string[] = [];

      RESOURCE_TYPES.forEach(({ key, label }) => {
        const resourceData = resourcesConfirmation[key];
        if (resourceData && resourceData !== null) {
          if (resourceData.ccError) {
            failedResources.push(label);
            errorMessages.push(resourceData.ccError);
          } else {
            successResources.push(label);
          }
        }
      });
      const failedResourcesList = failedResources.join(', ');
      const successResourcesList = successResources.join(', ');
      const combinedErrors = errorMessages.join(', ');

      if (response.success) {
        setResultType('success');
        const hasResourceError = failedResources.length > 0;
        if (hasResourceError) {
          const partialSuccessMessage =
            props?.rendering?.fields?.ConfirmationModalPartialSuccessMessage?.value || '-';

          let formattedMessage = partialSuccessMessage
            .replace('[resources]', failedResourcesList)
            .replace('[ccError]', combinedErrors);

          formattedMessage = formattedMessage.replace(/\n\s*\n/g, '\n').trim();

          setSyncResultMessage(formattedMessage);
        } else {
          let successMessage =
            props?.rendering?.fields?.ConfirmationModalSuccessMessage?.value ||
            'Record merged and sent to CC successfully';
          successMessage = successMessage
            .replace('[ccId]', response?.ccId || profile.patientCCId || '')
            .replace('[resources]', successResourcesList);

          successMessage = successMessage.replace(/\n\s*\n/g, '\n').trim();

          setSyncResultMessage(successMessage);
        }
        setSyncDialogMode('result');
        setShowSyncDialog(true);
      } else {
        setResultType('error');
        let errorMessage =
          props?.rendering?.fields?.ConfirmationModalErrorMessage?.value ||
          'Failed to merge and send record to CC';
        errorMessage = errorMessage.replace('[resources]', failedResourcesList);
        errorMessage = errorMessage.replace(/\n\s*\n/g, '\n').trim();
        setSyncResultMessage(errorMessage);
        setSyncDialogMode('result');
        setShowSyncDialog(true);
      }
    } catch (error: any) {
      setResultType('error');
      const resourcesConfirmation = error?.response?.data?.error?.resources || {};
      const failedResources: string[] = [];
      RESOURCE_TYPES.forEach(({ key, label }) => {
        const resourceData = resourcesConfirmation[key];
        if (resourceData && resourceData !== null) {
          if (resourceData.ccError) {
            failedResources.push(label);
          }
        }
      });
      const failedResourcesList = failedResources.join(', ');
      let errorMessage =
        props?.rendering?.fields?.ConfirmationModalErrorMessage?.value ||
        'Failed to merge and send record to CC';
      errorMessage = errorMessage.replace('[resources]', failedResourcesList);
      errorMessage = errorMessage.replace(/\n\s*\n/g, '\n').trim();
      setSyncResultMessage(errorMessage);
      setSyncDialogMode('result');
      setShowSyncDialog(true);
    } finally {
      setIsSyncing(false);
    }
  }, [selectedItems, profile, syncedResources, props?.rendering?.fields]);

  if (error || !profile) {
    return (
      <StyledContainer>
        <Alert severity="error" sx={{ mt: 3 }}>
          {error || 'Profile not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Referrals
        </Button>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          background: 'white',
          borderBottom: '2px solid #e0e0e0',
          p: 1.2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, fontSize: '16px' }}>
              {props?.rendering?.fields?.Title?.value ||
                `View Full Profile from ${
                  profile.leadStatus === LEAD_STATUS.PROCESSED ? 'Processed' : 'Referral'
                }`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
              {props?.rendering?.fields?.SubTittle?.value
                ? props.rendering.fields.SubTittle.value
                    .replace('[firsname]', profile.firstName || '')
                    .replace('[lastname]', profile.lastName || '')
                    .replace('[email]', profile.email || '')
                    .replace('[phonenumber]', profile.phone || '')
                    .replace('[patientCCId]', profile.patientCCId || '')
                    .split('|')
                    .map((part: string) => part.trim())
                    .filter(Boolean)
                    .join(' | ')
                : [
                    profile.firstName && profile.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile.firstName || profile.lastName,
                    profile?.email,
                    profile?.phone,
                  ]
                    .filter(Boolean)
                    .join(' | ')}
            </Typography>
          </Box>
          {profile.leadStatus === LEAD_STATUS.PROCESSED && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={
                  isSyncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />
                }
                onClick={handleSyncClick}
                disabled={isSyncing || selectedItems.size === 0}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#255E5E',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1a4545',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                  },
                }}
              >
                {props?.rendering?.fields?.SyncButtonText?.value || 'Sync'}{' '}
                {selectedItems.size > 0 && `(${selectedItems.size})`}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <StyledPaper>
        <PersonalInformationSection
          profile={profile}
          fieldConfigs={props?.rendering?.fields?.['Personal Information']}
          headerText={props?.rendering?.fields?.PersonalInformationHeaderText?.value}
        />

        <TrackingParametersSection
          trackingParams={profile}
          fieldConfigs={props?.rendering?.fields?.UTMParameters}
          headerText={props?.rendering?.fields?.UTMParametersHeaderText?.value}
        />

        <CaregiverInformationSection
          caregiver={profile.caregiver}
          fieldConfigs={props?.rendering?.fields?.CaregiverInfo}
          headerText={props?.rendering?.fields?.CaregiverInformationHeaderText?.value}
        />

        {isLoadingDedupe ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading deduplication details...
            </Typography>
          </Box>
        ) : dedupeError ? (
          <Box sx={{ p: 2 }}>
            <Typography color="error" variant="body2">
              {typeof dedupeError === 'object' && 'data' in dedupeError
                ? String(dedupeError.data)
                : 'Failed to load deduplication data'}
            </Typography>
          </Box>
        ) : (
          <DeduplicationSection
            enrichedDeduplicationData={enrichedDeduplicationData}
            expanded={expandedDedup}
            onExpandChange={() => setExpandedDedup(!expandedDedup)}
            rowData={profile}
            fieldConfigs={props?.rendering?.fields?.DedupAddParams}
            headerText={props?.rendering?.fields?.DeDuplicationParametersHeaderText?.value}
          />
        )}

        {loadingSyncedResources ? (
          <>
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
            <TableSkeleton />
          </>
        ) : (
          <>
            <ConditionsInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.conditions}
              fieldConfigs={props?.rendering?.fields?.Conditions}
              headerText={props?.rendering?.fields?.ConditionsHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <ProceduresInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.procedures}
              fieldConfigs={props?.rendering?.fields?.Procedures}
              headerText={props?.rendering?.fields?.ProceduresHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <MedicationsInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.medications}
              fieldConfigs={props?.rendering?.fields?.Medications}
              headerText={props?.rendering?.fields?.MedicationsHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <ImmunizationsInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.immunizations}
              fieldConfigs={props?.rendering?.fields?.Immunizations}
              headerText={props?.rendering?.fields?.ImmunizationsHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <AllergiesInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.allergies}
              fieldConfigs={props?.rendering?.fields?.Allergies}
              headerText={props?.rendering?.fields?.AllergiesHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <FamilyHistoryInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.familyMemberHistory}
              fieldConfigs={props?.rendering?.fields?.FamilyHistory}
              headerText={props?.rendering?.fields?.FamilyHistoryHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
            <MedicalDevicesInCDPSection
              selectedItems={selectedItems}
              onCheckboxChange={handleCheckboxChange}
              showSyncColumn={profile.leadStatus === LEAD_STATUS.PROCESSED}
              syncedData={syncedResources?.devices}
              fieldConfigs={props?.rendering?.fields?.MedicalDevices}
              headerText={props?.rendering?.fields?.MedicalDevicesHeaderText?.value}
              syncText={props?.rendering?.fields?.SyncButtonText?.value}
            />
          </>
        )}
      </StyledPaper>

      <ConfirmationDialog
        open={showSyncDialog}
        onClose={
          syncDialogMode === 'confirm' || resultType === 'error'
            ? () => setShowSyncDialog(false)
            : async () => {
                setShowSyncDialog(false);
                await fetchSyncedResources(profile);
                setSelectedItems(new Set());
              }
        }
        onConfirm={
          syncDialogMode === 'confirm'
            ? handleSync
            : resultType === 'error'
            ? () => setShowSyncDialog(false)
            : async () => {
                setShowSyncDialog(false);
                await fetchSyncedResources(profile);
                setSelectedItems(new Set());
              }
        }
        onCancel={() => setShowSyncDialog(false)}
        mode={syncDialogMode}
        message={
          syncDialogMode === 'confirm'
            ? props?.rendering?.fields?.ConfirmText?.value ||
              'Are you sure you want to send this record to CC?'
            : syncResultMessage
        }
        title="Confirm Send to CC"
        confirmButtonText={props?.rendering?.fields?.ConfirmButtonText?.value || 'Yes'}
        cancelButtonText={props?.rendering?.fields?.ConfirmDismissButtonText?.value || 'Cancel'}
      />
    </StyledContainer>
  );
};

export default FullCDPProfile;
